/* eslint-disable react/jsx-indent */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-nested-ternary */

import {
  EventRepeat,
  Schedule,
} from '@mui/icons-material';
import AccessTime from '@mui/icons-material/AccessTime';
import Today from '@mui/icons-material/Today';
import {
  Switch,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import RRuleGenerator from 'react-rrule-generator';
import { rrulestr } from 'rrule';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../utils/dateHelper';
import TimeSelect from '../../../../TimeSelect';
import TimezoneSelect from '../../../../TimezoneSelect';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const getDateTime = (date) => (date
  ? new Date(date).getTime()
  : null);

const isExistingRepeatingSchedule = (schedule) => {
  if (!schedule || !schedule.id) return false;
  const repeatSchedule = schedule.parentSchedule || schedule;
  if (!repeatSchedule || !repeatSchedule.id) return false;
  if (!repeatSchedule.endDate) return true;
  return getDateTime(repeatSchedule.startDate) !== getDateTime(repeatSchedule.endDate);
};

const SchedulingSection = ({
  validation,
  schedule,
  event,
  startDate,
  endDate,
  eventTemplate,
  duration,
  rrule,
  setStartDate,
  setEndDate,
  setRrule,
  setDuration,
  setAdvanceNotice,
  timezone,
  setTimezone,
  disabledForBeingInPast,
  defaultOpenRepeat,
  timezoneWarning,
  repeatOnly,
  stopRepeating,
  onStopRepeating,
  repeatEndDate,
}) => {
  const theme = useTheme();
  const [openRrule, setOpenRrule] = React.useState(false);
  const [changeTimezone, setChangeTimezone] = React.useState(false);
  const [openTimezoneWarning, setOpenTimezoneWarning] = React.useState(false);

  const repeatScheduleToUse = (schedule && schedule.parentSchedule) || schedule;
  const scheduleStartDate = new Date((repeatScheduleToUse && repeatScheduleToUse.startDate) || startDate);
  const isSingleOccurrence = !isExistingRepeatingSchedule(schedule)
    && endDate !== null
    && startDate.getTime() === endDate.getTime();
  const canEditRepeat = !disabledForBeingInPast || isExistingRepeatingSchedule(schedule);
  const showDateTimeControls = !repeatOnly || canEditRepeat;
  const isDateTimeDisabled = disabledForBeingInPast && !repeatOnly;

  React.useEffect(() => {
    setOpenRrule(!!defaultOpenRepeat);
    setChangeTimezone(false);
    setOpenTimezoneWarning(false);
  }, [schedule, defaultOpenRepeat]);

  if (!eventTemplate) return <div />;

  if (stopRepeating) {
    return (
      <Paper
        sx={{
          m: 1,
          p: 2,
        }}
      >
        <Typography>Repeating will end after {Moment(repeatEndDate).format('ll')}.</Typography>
        <Typography variant="body2">This occurrence and past tasks will remain. Later occurrences will be removed when you save.</Typography>
        <Link component="button" onClick={() => onStopRepeating(false)} sx={{ mt: 1 }}>Keep Repeating</Link>
      </Paper>
    );
  }

  const listItemSx = {
    px: 1,
    py: 0.75,
  };
  const listItemIconSx = {
    minWidth: 32,
    mr: 0.75,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
      height: '1.25rem',
      width: '1.25rem',
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing={1} sx={{ m: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            px: 1,
          }}
        >
          {repeatOnly
            ? 'Repeat Schedule'
            : 'Schedule'}
        </Typography>
        {showDateTimeControls && (
          <Paper sx={{ borderRadius: 3 }}>
            <List disablePadding>
              <ListItem dense sx={listItemSx}>
              <ListItemIcon sx={listItemIconSx}><Today /></ListItemIcon>
              <ListItemText>
                <Grid container spacing={1} alignItems="center" justifyContent="space-between">
                  <Grid>
                    <DatePicker
                      label={repeatOnly
                        ? 'Future Date'
                        : (event || isSingleOccurrence)
                          ? 'Date'
                          : 'Start Date'}
                      value={toMomentValue(startDate)}
                      disabled={isDateTimeDisabled}
                      onChange={(newValue) => {
                        const nextDate = fromPickerValue(newValue);
                        if (!nextDate) return;
                        if (isSingleOccurrence) {
                          setEndDate(nextDate);
                        }
                        setStartDate(nextDate);
                      }}
                      slotProps={{
                        textField: {
                          disabled: isDateTimeDisabled,
                          helperText: validation.startDate,
                          error: !!validation.startDate,
                          size: 'small',
                          sx: [{
                            mr: 1,
                            textWrap: 'auto',
                          }, changeTimezone
                            ? { maxWidth: 180 }
                            : { maxWidth: 200 }],
                          required: true,
                        },
                      }}
                    />
                  </Grid>
                  <Grid>
                    {!repeatOnly && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {changeTimezone
                          ? (
                            <TimezoneSelect
                              label="Timezone"
                              value={timezone}
                              onChange={(e, value) => setTimezone(value)}
                              sx={[{ margin: 0 }, timezoneWarning
                                ? { minWidth: 150 }
                                : { minWidth: 190 }]}
                              disabled={isDateTimeDisabled}
                            />
                          )
                          : (
                            <Tooltip title="Change timezone">
                              <Link
                                component="button"
                                variant="body2"
                                color="secondary"
                                sx={[{
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                }, timezoneWarning
                                  ? { maxWidth: 140 }
                                  : { maxWidth: 190 }]}
                                underline="hover"
                                onClick={() => {
                                  if (!isDateTimeDisabled) {
                                    setChangeTimezone(true);
                                  }
                                }}
                              >
                                {Moment.tz(timezone).zoneAbbr()}
                              </Link>
                            </Tooltip>
                          )}

                        {timezoneWarning && (
                          <IconButton
                            sx={{
                              p: 0,
                              ml: 1,
                            }}
                            onClick={() => setOpenTimezoneWarning(!openTimezoneWarning)}
                          >
                            <Schedule sx={{ color: `${theme.palette.error.main} !important` }} />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Grid>
                  {!repeatOnly && timezoneWarning && openTimezoneWarning && (
                    <Grid>
                      <Collapse in={openTimezoneWarning} timeout="auto" unmountOnExit sx={{ textWrap: 'wrap' }}>
                        <Typography variant="caption">{timezoneWarning}</Typography>
                      </Collapse>
                    </Grid>
                  )}
                </Grid>
              </ListItemText>
              </ListItem>
              <ListItem dense sx={listItemSx}>
              <ListItemIcon sx={listItemIconSx}><AccessTime /></ListItemIcon>
              <ListItemText>
                <Stack spacing={1}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Link
                      component="button"
                      variant="body2"
                      color="secondary"
                      underline="hover"
                      onClick={() => {
                        if (isDateTimeDisabled) {
                          return;
                        }
                        if (!duration) {
                          setDuration((eventTemplate && eventTemplate.eventDataDefaultValues
                            && eventTemplate.eventDataDefaultValues.defaultDuration
                            && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000)
                          const newStart = Moment(startDate).set('hour', Moment().hours() + 1).toDate();
                          if (isSingleOccurrence) {
                            setEndDate(newStart);
                          }
                          setStartDate(newStart);
                        } else {
                          setDuration(0);
                          const midnightDayOf = Moment(startDate).startOf('day').toDate();
                          if (isSingleOccurrence) {
                            setEndDate(midnightDayOf);
                          }
                          setStartDate(midnightDayOf);
                          setAdvanceNotice(0);
                        }
                      }}
                    >
                      {repeatOnly
                        ? 'Future Time'
                        : disabledForBeingInPast
                          ? 'No time specified for this task'
                          : 'All Day'}
                    </Link>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Switch
                        checked={!duration}
                        size="small"
                        disabled={isDateTimeDisabled}
                        onChange={() => {
                          if (!duration) {
                            setDuration((eventTemplate && eventTemplate.eventDataDefaultValues
                              && eventTemplate.eventDataDefaultValues.defaultDuration
                              && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000)
                            const newStart = Moment(startDate).set('hour', Moment().hours() + 1).toDate();
                            if (isSingleOccurrence) {
                              setEndDate(newStart);
                            }
                            setStartDate(newStart);
                          } else {
                            setDuration(0);
                            const midnightDayOf = Moment(startDate).startOf('day').toDate();
                            if (isSingleOccurrence) {
                              setEndDate(midnightDayOf);
                            }
                            setStartDate(midnightDayOf);
                            setAdvanceNotice(0);
                          }
                        }}
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="caption">
                        {duration
                          ? `${Math.round(duration / 60000)} min`
                          : 'All Day'}
                      </Typography>
                    </Box>
                  </Box>
                  {
                    duration
                      ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <TimeSelect
                            label="Start Time"
                            value={startDate}
                            onChange={(e) => {
                              const newValue = new Date(e.target.value);
                              if (isSingleOccurrence) {
                                setEndDate(newValue);
                              }
                              setStartDate(newValue);
                            }}
                            sx={{ minWidth: 140 }}
                            disabled={isDateTimeDisabled}
                          />
                          <TimeSelect
                            label="End Time"
                            value={new Date(startDate.getTime() + duration)}
                            onChange={(e) => {
                              const newValue = new Date(e.target.value);
                              if (newValue.getTime() - startDate.getTime() !== 0) {
                                setDuration(newValue.getTime() - startDate.getTime());
                              }
                            }}
                            sx={{ minWidth: 140 }}
                            disabled={isDateTimeDisabled}
                          />
                        </Box>
                      )
                      : <Box />
                  }
                </Stack>
              </ListItemText>
              </ListItem>
            </List>
          </Paper>
        )}
        <Paper
          sx={{ borderRadius: 3 }}
        >
          <List disablePadding>
            <ListItem
              disabled={!canEditRepeat}
              dense
              sx={listItemSx}
            >
              <ListItemIcon sx={listItemIconSx}><EventRepeat /></ListItemIcon>
              <ListItemText>
                {isSingleOccurrence
                  ? (
                    <Tooltip title="Select to repeat this scheduled task">
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        underline="hover"
                        onClick={() => {
                          if (!canEditRepeat) {
                            return;
                          }
                          setEndDate(Moment(scheduleStartDate).add(7, 'days').toDate());
                          setRrule('RRULE:FREQ=DAILY;INTERVAL=1');
                          setOpenRrule(true);
                        }}
                      >
                        Repeat Task
                      </Link>
                    </Tooltip>
                  )
                  : (
                    <Typography color="primary" component="span" variant="subtitle1" sx={{ textWrap: 'auto' }}>
                      {`${capitalize(rrulestr(rrule).toText())} ${endDate
                        ? `ending ${Moment(endDate).format('ll')}`
                        : ''}`}
                    </Typography>
                  )}

                {!isSingleOccurrence && (
                  <Box sx={{ mt: 1 }}>
                    <DatePicker
                      label="End Date"
                      value={toMomentValue(endDate)}
                      minDate={toMomentValue(startDate)}
                      onChange={(newValue) => {
                        setEndDate(fromPickerValue(newValue));
                      }}
                      onClick={(e) => { e.stopPropagation(); }}
                      slotProps={{
                        textField: {
                          onClick: (e) => { e.stopPropagation(); },
                          helperText: validation.endDate,
                          error: !!validation.endDate,
                          disabled: !endDate || !canEditRepeat,
                          size: 'small',
                          sx: {
                            maxWidth: 200,
                            mr: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </ListItemText>
              {!isSingleOccurrence
                && canEditRepeat
                && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip title="Remove repeating of this task">
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        sx={{ ml: 1 }}
                        underline="hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (isExistingRepeatingSchedule(schedule)) {
                            onStopRepeating(true);
                          } else {
                            setEndDate(startDate);
                          }
                          setOpenRrule(false);
                        }}
                      >
                        Remove Repeat
                      </Link>
                    </Tooltip>
                  </Box>
                )}
            </ListItem>
            <Collapse in={openRrule} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                <RRuleGenerator
                  onChange={(newRrule) => setRrule(newRrule)}
                  value={rrule}
                  config={{
                    repeat: ['Daily', 'Monthly', 'Weekly', 'Yearly'],
                    weekStartsOnSunday: true,
                    hideStart: true,
                    hideEnd: true,
                  }}
                />
              </Box>
            </Collapse>
          </List>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
};

SchedulingSection.propTypes = {
  validation: PropTypes.object,
  schedule: PropTypes.object,
  event: PropTypes.object,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  eventTemplate: PropTypes.object,
  duration: PropTypes.number,
  rrule: PropTypes.string,
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
  setRrule: PropTypes.func.isRequired,
  setDuration: PropTypes.func.isRequired,
  setAdvanceNotice: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  setTimezone: PropTypes.func.isRequired,
  disabledForBeingInPast: PropTypes.bool,
  defaultOpenRepeat: PropTypes.bool,
  timezoneWarning: PropTypes.string,
  repeatOnly: PropTypes.bool,
  stopRepeating: PropTypes.bool,
  onStopRepeating: PropTypes.func,
  repeatEndDate: PropTypes.instanceOf(Date),
};

SchedulingSection.defaultProps = {
  validation: {},
  schedule: null,
  event: null,
  startDate: new Date(),
  endDate: new Date(),
  eventTemplate: null,
  duration: 0,
  rrule: 'RRULE:FREQ=DAILY;INTERVAL=1',
  disabledForBeingInPast: false,
  defaultOpenRepeat: false,
  timezoneWarning: null,
  repeatOnly: false,
  stopRepeating: false,
  onStopRepeating: () => {},
  repeatEndDate: null,
};

export default SchedulingSection;
